import React, { useState } from 'react';
import { Editor, useEditorState } from '@tiptap/react';
import { indentSelection, outdentSelection } from '../utils/toolbarHandlers';
import { DEFAULT_OUTLINE_COLOR, SCRIPT_TYPES } from '../extensions';
import { ColorPalette } from './ColorPalette';
import { ToolbarMenu } from './ToolbarMenu';
import type { ScriptType } from '../extensions';

const FONT_FAMILIES = [
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Verdana',
    'Comic Sans MS',
    'Impact',
    'Trebuchet MS',
];

// The screenplay element names live in the language pack. The extension is a
// plain module and cannot reach it, so the labels are looked up here, where
// `t` is already to hand.
const SCRIPT_TYPE_LABEL_KEYS: Record<ScriptType, string> = {
    scene: 'scriptScene',
    action: 'scriptAction',
    character: 'scriptCharacter',
    dialogue: 'scriptDialogue',
    parenthetical: 'scriptParenthetical',
    transition: 'scriptTransition',
};

type Alignment = 'left' | 'center' | 'right' | 'justify';

const ALIGNMENTS: { key: Alignment; labelKey: string }[] = [
    { key: 'left', labelKey: 'alignLeft' },
    { key: 'center', labelKey: 'alignCenter' },
    { key: 'right', labelKey: 'alignRight' },
    { key: 'justify', labelKey: 'alignJustify' },
];

// Four lines, indented to show where the text sits.
const alignmentIcon = (rows: [number, number][]) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        {rows.map(([x, width], i) => (
            <rect key={i} x={x} y={2 + i * 4} width={width} height="1.5" />
        ))}
    </svg>
);

const ALIGNMENT_ICONS: Record<Alignment, React.ReactNode> = {
    left: alignmentIcon([[0, 14], [0, 10], [0, 14]]),
    center: alignmentIcon([[1, 14], [3, 10], [1, 14]]),
    right: alignmentIcon([[2, 14], [6, 10], [2, 14]]),
    justify: alignmentIcon([[0, 16], [0, 16], [0, 16]]),
};

const LIST_ICONS = {
    bulletList: '•••',
    orderedList: '123',
    taskList: '☑',
};

// '' means paragraph
const headingLevelAt = (editor: Editor): string => {
    for (let level = 1; level <= 6; level++) {
        if (editor.isActive('heading', { level })) return String(level);
    }
    return '';
};

// The explicit font-size mark when there is one, otherwise the size the text
// actually renders at — so a heading reports its real size instead of the
// last number typed into the box.
const fontSizeAt = (editor: Editor): number | null => {
    const marked = editor.getAttributes('textStyle')?.fontSize;
    if (marked) {
        const parsed = parseFloat(marked);
        if (!Number.isNaN(parsed)) return Math.round(parsed);
    }
    try {
        const { node } = editor.view.domAtPos(editor.state.selection.head);
        const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement);
        if (!element) return null;
        const computed = parseFloat(window.getComputedStyle(element).fontSize);
        return Number.isNaN(computed) ? null : Math.round(computed);
    } catch {
        return null;
    }
};

// Font family values can arrive quoted from pasted HTML
const normalizeFontFamily = (family: unknown): string =>
    typeof family === 'string' ? family.replace(/^["']|["']$/g, '').split(',')[0].trim() : '';

interface FormattingToolbarProps {
    documentEditor: Editor | null;
    t: any;
    fontSize: number;
    showColorPicker: 'text' | 'highlight' | 'outline' | null;
    currentTextColor: string;
    currentHighlightColor: string;
    onSetTextStyle: (level: number | null) => void;
    onSetFontFamily: (font: string) => void;
    onHandleFontSizeChange: (size: number) => void;
    onIncrementFontSize: (currentSize: number) => void;
    onDecrementFontSize: (currentSize: number) => void;
    onSetShowColorPicker: (type: 'text' | 'highlight' | 'outline' | null) => void;
    onSetTextColor: (color: string) => void;
    onSetHighlightColor: (color: string) => void;
    onSetAlignment: (align: 'left' | 'center' | 'right' | 'justify') => void;
    onApplyOutline: (width: number, color: string) => void;
    onRemoveOutline: () => void;
    onInsertImageFromUrl: () => void;
    onClearFormatting: () => void;
}

export const FormattingToolbar: React.FC<FormattingToolbarProps> = ({
    documentEditor,
    t,
    fontSize,
    showColorPicker,
    currentTextColor,
    currentHighlightColor,
    onSetTextStyle,
    onSetFontFamily,
    onHandleFontSizeChange,
    onIncrementFontSize,
    onDecrementFontSize,
    onSetShowColorPicker,
    onSetTextColor,
    onSetHighlightColor,
    onSetAlignment,
    onApplyOutline,
    onRemoveOutline,
    onInsertImageFromUrl,
    onClearFormatting,
}) => {
    // Local UI state for the outline popup; the applied values live on the
    // textStyle mark itself.
    const [outlineWidth, setOutlineWidth] = useState(0.1);
    const [outlineColor, setOutlineColor] = useState(DEFAULT_OUTLINE_COLOR);

    // Tiptap v3 does not re-render on transactions, so every indicator below
    // has to come from a subscription — otherwise the toolbar keeps showing
    // whatever was last clicked instead of what the cursor is actually on.
    const state = useEditorState({
        editor: documentEditor,
        selector: ({ editor }) => {
            if (!editor) return null;
            return {
                canUndo: editor.can().undo(),
                canRedo: editor.can().redo(),
                headingLevel: headingLevelAt(editor),
                scriptType: (editor.getAttributes('paragraph')?.scriptType as string) || '',
                fontFamily: normalizeFontFamily(editor.getAttributes('textStyle')?.fontFamily),
                fontSize: fontSizeAt(editor),
                textColor: (editor.getAttributes('textStyle')?.color as string) || '',
                highlightColor: (editor.getAttributes('highlight')?.color as string) || '',
                bold: editor.isActive('bold'),
                italic: editor.isActive('italic'),
                underline: editor.isActive('underline'),
                strike: editor.isActive('strike'),
                alignLeft: editor.isActive({ textAlign: 'left' }),
                alignCenter: editor.isActive({ textAlign: 'center' }),
                alignRight: editor.isActive({ textAlign: 'right' }),
                alignJustify: editor.isActive({ textAlign: 'justify' }),
                bulletList: editor.isActive('bulletList'),
                orderedList: editor.isActive('orderedList'),
                taskList: editor.isActive('taskList'),
                writerNote: Boolean(editor.getAttributes('paragraph').ybNote
                    || editor.getAttributes('heading').ybNote),
            };
        },
    });

    // Font size box follows the cursor; falls back to the last value set from
    // the toolbar before the editor is ready.
    // Nothing marked means the paragraph default, which is left.
    const currentAlignment: Alignment = state?.alignCenter ? 'center'
        : state?.alignRight ? 'right'
        : state?.alignJustify ? 'justify' : 'left';

    const currentFontSize = state?.fontSize ?? fontSize;
    const currentFontFamily = state?.fontFamily || 'Arial';
    const knownFontFamily = FONT_FAMILIES.includes(currentFontFamily);

    return (
        <div className="doc-formatting-toolbar">
            {/* Undo/Redo */}
            <div className="toolbar-section">
                <button
                    className="toolbar-btn"
                    onClick={() => documentEditor?.chain().focus().undo().run()}
                    disabled={!state?.canUndo}
                    title={`${t.editor.undo} (⌘Z)`}
                >
                    ↶
                </button>
                <button
                    className="toolbar-btn"
                    onClick={() => documentEditor?.chain().focus().redo().run()}
                    disabled={!state?.canRedo}
                    title={`${t.editor.redo} (⌘⇧Z)`}
                >
                    ↷
                </button>
            </div>

            <div className="toolbar-divider"></div>

            {/* Text Style Dropdown */}
            <div className="toolbar-section">
                <select
                    className="toolbar-select"
                    onChange={(e) => onSetTextStyle(e.target.value ? parseInt(e.target.value) : null)}
                    value={state?.headingLevel ?? ''}
                    title={t.editor.textStyle}
                >
                    <option value="">{t.editor.paragraph}</option>
                    <option value="1">{t.editor.heading1}</option>
                    <option value="2">{t.editor.heading2}</option>
                    <option value="3">{t.editor.heading3}</option>
                    <option value="4">{t.editor.heading4}</option>
                    <option value="5">{t.editor.heading5}</option>
                    <option value="6">{t.editor.heading6}</option>
                </select>
            </div>

            <div className="toolbar-divider"></div>

            {/* Screenplay element (value reflects the cursor's current line;
                Tab/Enter do the fast cycling, this is the discoverable path) */}
            <div className="toolbar-section">
                <select
                    className="toolbar-select"
                    value={state?.scriptType ?? ''}
                    onChange={(e) =>
                        documentEditor
                            ?.chain()
                            .focus()
                            .setScriptType((e.target.value || null) as ScriptType | null)
                            .run()
                    }
                    title={t.editor.screenplayElementHint}
                >
                    <option value="">{t.editor.notScript}</option>
                    {SCRIPT_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {t.editor[SCRIPT_TYPE_LABEL_KEYS[type]]}
                        </option>
                    ))}
                </select>
            </div>

            <div className="toolbar-divider"></div>

            {/* Font Family */}
            <div className="toolbar-section">
                <select
                    className="toolbar-select"
                    onChange={(e) => onSetFontFamily(e.target.value)}
                    value={currentFontFamily}
                    title={t.editor.font}
                >
                    {/* A font carried in from a paste still needs an entry to show up */}
                    {!knownFontFamily && <option value={currentFontFamily}>{currentFontFamily}</option>}
                    {FONT_FAMILIES.map((family) => (
                        <option key={family} value={family}>
                            {family}
                        </option>
                    ))}
                </select>
            </div>

            <div className="toolbar-divider"></div>

            {/* Font Size */}
            <div className="toolbar-section">
                <div className="font-size-control">
                    <button
                        className="toolbar-btn size-btn"
                        onClick={() => onDecrementFontSize(currentFontSize)}
                        title={t.editor.decreaseFontSize}
                    >
                        −
                    </button>
                    <input
                        type="number"
                        className="font-size-input"
                        value={currentFontSize}
                        onChange={(e) => onHandleFontSizeChange(parseInt(e.target.value) || 14)}
                        min="6"
                        max="72"
                    />
                    <button
                        className="toolbar-btn size-btn"
                        onClick={() => onIncrementFontSize(currentFontSize)}
                        title={t.editor.increaseFontSize}
                    >
                        +
                    </button>
                </div>
            </div>

            <div className="toolbar-divider"></div>

            {/* Bold, italic, underline, strikethrough — one button, lit while
                any of them applies; the shortcuts still work without it */}
            <ToolbarMenu
                title={`${t.editor.bold} / ${t.editor.italic} / ${t.editor.underline} / ${t.editor.strikethrough}`}
                active={Boolean(state?.bold || state?.italic || state?.underline || state?.strike)}
                trigger={<strong>B</strong>}
                keepOpen
                items={[
                    { key: 'bold', icon: <strong>B</strong>, label: `${t.editor.bold} (⌘B)`,
                      run: () => documentEditor?.chain().focus().toggleBold().run() },
                    { key: 'italic', icon: <em>I</em>, label: `${t.editor.italic} (⌘I)`,
                      run: () => documentEditor?.chain().focus().toggleItalic().run() },
                    { key: 'underline', icon: <u>U</u>, label: `${t.editor.underline} (⌘U)`,
                      run: () => documentEditor?.chain().focus().toggleUnderline().run() },
                    { key: 'strike', icon: <s>S</s>, label: `${t.editor.strikethrough} (⌘⇧S)`,
                      run: () => documentEditor?.chain().focus().toggleStrike().run() },
                ].map(({ key, icon, label, run }) => ({
                    key,
                    icon,
                    label,
                    active: Boolean(state?.[key as 'bold' | 'italic' | 'underline' | 'strike']),
                    onSelect: run,
                }))}
            />

            <div className="toolbar-divider"></div>

            {/* Text Color */}
            <div className="toolbar-section color-section">
                <button
                    className="toolbar-btn color-btn"
                    onClick={() => onSetShowColorPicker(showColorPicker === 'text' ? null : 'text')}
                    title={t.editor.textColor}
                >
                    <span style={{ borderBottom: `3px solid ${currentTextColor}` }}>A</span>
                </button>
                {showColorPicker === 'text' && (
                    <div className="color-picker-dropdown palette-popup">
                        <ColorPalette
                            t={t}
                            activeColor={state?.textColor || currentTextColor}
                            resetLabel={t.editor.defaultColor}
                            onSelect={onSetTextColor}
                            onReset={() => onSetTextColor('default')}
                        />
                    </div>
                )}
            </div>

            {/* Highlight Color */}
            <div className="toolbar-section color-section">
                <button
                    className="toolbar-btn color-btn"
                    onClick={() => onSetShowColorPicker(showColorPicker === 'highlight' ? null : 'highlight')}
                    title={t.editor.highlightColor}
                >
                    <span style={{ backgroundColor: currentHighlightColor, padding: '0 4px' }}>H</span>
                </button>
                {showColorPicker === 'highlight' && (
                    <div className="color-picker-dropdown palette-popup">
                        <ColorPalette
                            t={t}
                            activeColor={state?.highlightColor || currentHighlightColor}
                            resetLabel={t.editor.noHighlight}
                            onSelect={onSetHighlightColor}
                            onReset={() => onSetHighlightColor('transparent')}
                        />
                    </div>
                )}
            </div>

            {/* Text Outline */}
            <div className="toolbar-section color-section">
                <button
                    className="toolbar-btn color-btn"
                    onClick={() => {
                        const opening = showColorPicker !== 'outline';
                        onSetShowColorPicker(opening ? 'outline' : null);
                        if (opening) {
                            // Pressing the button applies the current (default 0.1px) outline
                            onApplyOutline(outlineWidth, outlineColor);
                        }
                    }}
                    title={t.editor.textOutline}
                >
                    <span style={{ WebkitTextStroke: `0.6px ${outlineColor}` }}>A</span>
                </button>
                {showColorPicker === 'outline' && (
                    <div className="color-picker-dropdown outline-popup">
                        <div className="outline-swatch-row">
                            {[DEFAULT_OUTLINE_COLOR, '#000000', '#ffffff', '#ff0000', '#0000ff', '#ffff00', '#00ff00'].map(color => (
                                <button
                                    key={color}
                                    className={`color-swatch ${outlineColor === color ? 'selected' : ''}`}
                                    style={{ backgroundColor: color }}
                                    title={color === DEFAULT_OUTLINE_COLOR ? t.editor.themeDefault : color}
                                    onClick={() => {
                                        setOutlineColor(color);
                                        onApplyOutline(outlineWidth, color);
                                    }}
                                />
                            ))}
                            <button
                                key="remove"
                                className="color-swatch"
                                style={{ backgroundColor: '#2a2a2a' }}
                                title={t.editor.removeOutline}
                                onClick={() => {
                                    onRemoveOutline();
                                    onSetShowColorPicker(null);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="outline-slider-row">
                            <input
                                type="range"
                                min={0.1}
                                max={2}
                                step={0.1}
                                value={outlineWidth}
                                onChange={(e) => {
                                    const width = parseFloat(e.target.value);
                                    setOutlineWidth(width);
                                    onApplyOutline(width, outlineColor);
                                }}
                            />
                            <span className="outline-width-label">{outlineWidth.toFixed(1)}px</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="toolbar-divider"></div>

            {/* Alignment — one button showing the alignment under the cursor */}
            <ToolbarMenu
                title={`${t.editor.alignLeft} / ${t.editor.alignCenter} / ${t.editor.alignRight} / ${t.editor.alignJustify}`}
                active={Boolean(state?.alignCenter || state?.alignRight || state?.alignJustify)}
                trigger={ALIGNMENT_ICONS[currentAlignment]}
                items={ALIGNMENTS.map(({ key, labelKey }) => ({
                    key,
                    icon: ALIGNMENT_ICONS[key],
                    label: t.editor[labelKey],
                    active: currentAlignment === key,
                    onSelect: () => onSetAlignment(key),
                }))}
            />

            <div className="toolbar-divider"></div>

            {/* Indent */}
            <div className="toolbar-section">
                <button
                    className="toolbar-btn"
                    onClick={() => outdentSelection(documentEditor)}
                    title={t.editor.outdent}
                >
                    ⇤
                </button>
                <button
                    className="toolbar-btn"
                    onClick={() => indentSelection(documentEditor)}
                    title={t.editor.indent}
                >
                    ⇥
                </button>
            </div>

            <div className="toolbar-divider"></div>

            {/* Lists — one button showing the list type under the cursor */}
            <ToolbarMenu
                title={`${t.editor.bulletList} / ${t.editor.numberedList} / ${t.editor.checkList}`}
                active={Boolean(state?.bulletList || state?.orderedList || state?.taskList)}
                trigger={state?.orderedList ? LIST_ICONS.orderedList
                    : state?.taskList ? LIST_ICONS.taskList : LIST_ICONS.bulletList}
                items={[
                    { key: 'bulletList', label: t.editor.bulletList,
                      run: () => documentEditor?.chain().focus().toggleBulletList().run() },
                    { key: 'orderedList', label: t.editor.numberedList,
                      run: () => documentEditor?.chain().focus().toggleOrderedList().run() },
                    { key: 'taskList', label: t.editor.checkList,
                      run: () => documentEditor?.chain().focus().toggleTaskList().run() },
                ].map(({ key, label, run }) => ({
                    key,
                    icon: LIST_ICONS[key as keyof typeof LIST_ICONS],
                    label,
                    active: Boolean(state?.[key as 'bulletList' | 'orderedList' | 'taskList']),
                    onSelect: run,
                }))}
            />

            <div className="toolbar-divider"></div>

            {/* Insert Image */}
            <div className="toolbar-section">
                <button
                    className="toolbar-btn"
                    onClick={onInsertImageFromUrl}
                    title={t.editor.insertImageOrPaste}
                >
                    🖼️
                </button>
            </div>

            <div className="toolbar-divider"></div>

            {/* Note to self: scaffolding the writer can see and exports
                leave behind */}
            <div className="toolbar-section">
                <button
                    className={`toolbar-btn ${state?.writerNote ? 'active' : ''}`}
                    onClick={() => documentEditor?.chain().focus().toggleWriterNote().run()}
                    title={t.editor.writerNoteHint}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h12l4 4v12H4z" />
                        <path d="M8 10h8M8 14h5" />
                    </svg>
                </button>
            </div>

            <div className="toolbar-divider"></div>

            {/* Clear Formatting */}
            <div className="toolbar-section">
                <button
                    className="toolbar-btn"
                    onClick={onClearFormatting}
                    title={t.editor.clearFormatting}
                >
                    ✕
                </button>
            </div>
        </div>
    );
};
